import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isObject, keyBy, sortBy } from 'lodash';
import { useDataService } from '../../../../../../services/ApiDataService';
import {
    getAgentVoIPRegistrationInterface,
    getAgentVoIPRegistrationInterfaceTooltip,
    isNextGeneration,
    isVoIPAgentDirectCallMode,
} from '../../../../agents/AgentHelper';
import {
    filterVoipAgentsAgents,
    getCallManagers,
} from '../../../../../panels/voip-test-now-panels/VoipPulseTestConfigHelpers';
import {
    nPointsListApiUrl,
    voipAgentQueryParams,
} from '../../../../../panels/voip-test-now-panels/VoipPulseTestConfig';
import createMemoizedContextProvider from '../../hooks/CreateMemoizedContextProvider';
import { VOIP_ADAPTER_TYPE_DEFAULT, VOIP_AGENTS_CONTEXT_NAME, VOIP_ICON_PREFIX } from '../../../ServiceTestsConstants';

const { useContext: useVoipAgentsContext, Provider: VoipAgentsContextProvider } = createMemoizedContextProvider(
    VOIP_AGENTS_CONTEXT_NAME,
    {
        agents: [],
        agentsById: {},
        isLoading: true,
        selectedCallerId: '',
        setSelectedCallerId: () => {},
        selectedCalleeId: '',
        setSelectedCalleeId: () => {},
    }
);

/**
 * Map raw agent from API to UI model used by call participants.
 * Must be called AFTER getCallManagers has populated primaryCallManager objects.
 * If getCallManagers failed, primaryCallManager may still be a string ID — guard accordingly.
 */
function mapAgentToUiModel(agent) {
    const ipmIconText = VOIP_ICON_PREFIX;
    const primaryCallManager = get(agent, 'voipConfiguration.primaryCallManager');
    const callManagerResolved = isObject(primaryCallManager);

    return {
        id: agent._id,
        guid: agent.guid,
        name: agent.name,
        typeIcon: agent.typeIcon,
        isIPMIcon: agent.typeIcon?.indexOf(ipmIconText) > -1,
        translatedType: agent.translatedType,
        status: agent.status,
        isNextGeneration: isNextGeneration(agent),
        adapterType: get(agent, 'voipConfiguration.adapterType', VOIP_ADAPTER_TYPE_DEFAULT),
        interfaceTooltip: getAgentVoIPRegistrationInterfaceTooltip(agent),
        interfaceName: getAgentVoIPRegistrationInterface(agent),
        useDirectCalling: isVoIPAgentDirectCallMode(agent),
        voipConfiguration: agent.voipConfiguration || {},
        network: agent.network || {},
        // Flag whether call manager object was resolved (vs still a string ID)
        callManagerResolved,
    };
}

/**
 * Context provider that loads VoIP agents once and shares them
 * between CallParticipants and CallSettings sections.
 */
export function VoipAgentsProvider({ children }) {
    const [agents, setAgents] = useState([]);
    const [agentsById, setAgentsById] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCallerId, setSelectedCallerId] = useState('');
    const [selectedCalleeId, setSelectedCalleeId] = useState('');
    const dataService = useDataService();

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const agentData = await dataService.get(nPointsListApiUrl, voipAgentQueryParams);
                if (cancelled) {
                    return;
                }

                // Normalize API response — could be arrayed or {data: [...]}
                const rawList = Array.isArray(agentData) ? agentData : get(agentData, 'data', []);

                // Filter: for scheduled tests (not testNow), include all statuses
                const validAgents = filterVoipAgentsAgents(false, rawList).filter(Boolean);

                // Build keyed lookup on raw agents for getCallManagers
                const rawAgentsById = keyBy(validAgents, '_id');

                // Fetch full call manager objects (mutates rawAgentsById in-place)
                try {
                    await getCallManagers(validAgents, dataService, rawAgentsById);
                } catch {
                    // Call manager fetch failure is non-fatal; agents still usable
                    // but primaryCallManager may remain as string ID
                }
                if (cancelled) {
                    return;
                }

                // Map to UI model (after getCallManagers so primaryCallManager may be populated)
                const mapped = validAgents.map(mapAgentToUiModel);
                const byId = keyBy(mapped, 'id');
                const sortMappedList = sortBy(mapped, (item) => item.name?.toLowerCase());
                setAgents(sortMappedList);
                setAgentsById(byId);
            } catch {
                // Agent picker API failure — set empty data
                if (!cancelled) {
                    setAgents([]);
                    setAgentsById({});
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // dataService is a stable context singleton — identity never changes between renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const value = useMemo(
        () => ({
            agents,
            agentsById,
            isLoading,
            selectedCallerId,
            setSelectedCallerId,
            selectedCalleeId,
            setSelectedCalleeId,
        }),
        [agents, agentsById, isLoading, selectedCallerId, selectedCalleeId]
    );

    return <VoipAgentsContextProvider value={value}>{children}</VoipAgentsContextProvider>;
}

VoipAgentsProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * Hook to consume VoIP agents data from the nearest VoipAgentsProvider.
 * Returns { agents, agentsById, isLoading }.
 */
export const useVoipPulseAgents = useVoipAgentsContext;
